import { useState, useEffect, useRef } from "react";
import { Modal } from "../../ui/modal";
import { ChevronDownIcon, CloseIcon } from "../../../icons";
import Button from "../../ui/button/Button";
import Spinner from "../../ui/Spinner/Spinner";
import { PaymentDetails, Currency } from "../types";
import { request } from "../../../utils";
import { EMAIL_PAYMENT_METHODS, getBankCodeMaxLength, INT_BANK_FIELD_CONFIG, INTERNATIONAL_BANK_ISO2, PAYMENT_METHOD } from "../../../utils/constant";
import { getBlockchainName, getNetworkCode, validateEmail } from "../../../utils/helpers";


interface SendingPaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (details: PaymentDetails) => void;
  currency: string;
  paymentMethod: "MOBILE_MONEY" | "BANK_TRANSFER" | "STABLE_CURRENCY" | "WALLET" | "ZELLE" | "INTERAC" | "WECHAT_PAY" | "ALIPAY";
  fromCurrency: Currency;
  token: string;
  exchangeRate: any
}

type BankAccountRes = {
  accountName: string;
  accountNumber: string;
};

export function SendingPaymentDetailsModal({
  isOpen,
  onClose,
  onSave,
  currency,
  paymentMethod,
  fromCurrency,
  token,
  exchangeRate
}: SendingPaymentDetailsModalProps) {
  const [step, setStep] = useState<"select" | "form">("select");
  const [details, setDetails] = useState<PaymentDetails>({
    accountName: "",
    accountNumber: "",
    providerName: "",
    paymentMethod: paymentMethod || "",
    iso2: fromCurrency?.iso2 || "",
    currency: currency,
    bankCode: "",
    network: "",
    walletAddress: "",
    email: "",
    holderCity: "",
    holderStreet: "",
    holderPostalCode: "",
    phoneNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [providers, setProviders] = useState<any[]>([]);
  const [filteredBanks, setFilteredBanks] = useState<any[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [accountNameVerified, setAccountNameVerified] = useState(false);
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [bankSearchQuery, setBankSearchQuery] = useState("");
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const bankSearchRef = useRef<HTMLDivElement>(null);
  const providerDropdownRef = useRef<HTMLDivElement>(null);

  // Reset Form
  const resetForm = () => {
    setDetails({
      accountName: "",
      accountNumber: "",
      providerName: "",
      paymentMethod: paymentMethod || "",
      iso2: fromCurrency?.iso2 || "",
      currency: currency,
      saveDetails: false,
      bankCode: "",
      network: "",
      walletAddress: "",
      email: "",
      holderCity: "",
      holderStreet: "",
      holderPostalCode: "",
      phoneNumber: "",
    });
    setAccountNameVerified(false);
    setError("");
    // setSelectedBeneficiary(null);
    setStep("select");
    setBankSearchQuery("");
    setFilteredBanks([]);
  };

  // --- Close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // ---- Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        bankSearchRef.current &&
        !bankSearchRef.current.contains(event.target as Node)
      ) {
        setShowBankDropdown(false);
      }
      if (
        providerDropdownRef.current &&
        !providerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProviderDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // --- Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError("");

      if (paymentMethod) {
        if (paymentMethod === "MOBILE_MONEY") {
          fetchPaymentNetworks();
        } else if (paymentMethod === "BANK_TRANSFER") {
          if (fromCurrency?.hasBankList) {
            fetchBanksList();
          }
        }
      }
      setIsLoading(false);
    } else {
      resetForm();
    }
  }, [isOpen, paymentMethod, currency, fromCurrency?.iso2]);

  // ---- Filter banks based on search query
  useEffect(() => {
    if (bankSearchQuery && providers.length > 0) {
      const filtered = providers
        .filter((bank) =>
          bank.name.toLowerCase().includes(bankSearchQuery.toLowerCase())
        )
        .slice(0, 10);
      setFilteredBanks(filtered);
    } else {
      setFilteredBanks([]);
    }
  }, [bankSearchQuery, providers]);

  // ---- Fetch mobile networks
  const fetchPaymentNetworks = async () => {
    setLoadingProviders(true);
    try {
      const iso2 =
        fromCurrency?.iso2 ||
        (fromCurrency?.code ? fromCurrency.code.substring(0, 2) : "");

      if (!iso2) {
        throw new Error("Could not determine country code");
      }

      const response = await request.get({
        url: `/transaction/MOBILE_MONEY/providers?currency=${currency}&iso2=${iso2}&token=${token}`,
      });

      if (response.data && response.success && Array.isArray(response.data)) {
        const providersData = response.data.map((provider: any) => ({
          code: provider.code,
          name: provider.name,
          logo: provider.logo,
          hasGateway: provider.hasGateway,
        }));

        setProviders(providersData);
      } else {
        throw new Error("Invalid network data format");
      }
    } catch (error) {
      console.error("Failed to fetch mobile networks:", error);
      setError("Failed to load mobile networks");
    } finally {
      setLoadingProviders(false);
    }
  };

  // ---- Fetch banks list
  const fetchBanksList = async () => {
    setLoadingProviders(true);

    try {
      const response = await request.get({
        url: `/transaction/BANK_TRANSFER/providers?currency=${currency}&token=${token}`,
      });

      if (response.data && response.success && Array.isArray(response.data)) {
        const banksData = response.data.map((bank: any) => ({
          code: bank.bankCode,
          name: bank.name,
        }));
        setProviders(banksData);
      } else {
        throw new Error("Invalid bank data format");
      }
    } catch (error) {
      console.error("Failed to fetch banks:", error);
      setError("Failed to load banks");
    } finally {
      setLoadingProviders(false);
    }
  };

  // ---- Verify bank account
  const verifyBankAccount = async () => {
    if (!details.bankCode || !details.accountNumber) return;

    setIsVerifyingAccount(true);
    setError("");

    try {
      const response = await request.post({
        url: `/transaction/verify-bank-account?token=${token}`,
        data: {
          currency: "NGN",
          bankCode: details.bankCode,
          accountNumber: details.accountNumber,
        },
      });

      if (response.data && response.success) {
        const accountData = response.data as BankAccountRes;
        setAccountNameVerified(true);
        setDetails((prev) => ({
          ...prev,
          accountName: accountData.accountName || "Verified Account",
        }));
      } else {
        setError("Account verification failed");
      }
    } catch (error) {
      console.error("Failed to verify account:", error);
      setError("Failed to verify account details");
    } finally {
      setIsVerifyingAccount(false);
    }
  };

  // ---- Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    // ---- Handle numeric fields differently
    if (name === "accountNumber" || name === "bankCode") {
      let numericValue = value;

      // ---- Apply different max lengths based on field and country
      if (name === "accountNumber") {
        if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY || details.iso2 === "NG") {
          const phoneNumberLength = fromCurrency?.paymentMethod?.phoneNumberLength ?? 10;
          numericValue = handleNumericInput(value, phoneNumberLength);
        } else {
          const maxLength = getBankCodeMaxLength(details.iso2);
          numericValue = handleNumericInput(value, maxLength);
        }
      } else if (name === "bankCode") {
        // ---- For US routing numbers (9 digits) or UK sort codes (6 digits) made it 10
        if (details.iso2 === "US") {
          numericValue = handleNumericInput(value, 10);
        } else if (details.iso2 === "GB") {
          numericValue = handleNumericInput(value, 10);
        } else if (details.iso2 === "CA") {
          numericValue = handleNumericInput(value, 10);
        }
      }

      setDetails((prev) => ({
        ...prev,
        [name]: numericValue,
      }));

      return;
    }

    // ---- Normal handling for other fields
    setDetails((prev) => {
      const updatedDetails = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "bankCode" && details.iso2 === "NG") {
        updatedDetails.providerName = value;
      }

      return updatedDetails;
    });

    if (name === "accountNumber" && accountNameVerified) {
      setAccountNameVerified(false);
      setDetails((prev) => ({
        ...prev,
        accountName: "",
      }));
    }
  };

  // ---- Helper function for numeric input validation
  const handleNumericInput = (value: string, maxLength?: number) => {
    // Remove all non-digit characters
    let numericValue = value.replace(/\D/g, "");

    // Apply max length if provided
    if (maxLength !== undefined) {
      numericValue = numericValue.slice(0, maxLength);
    }

    return numericValue;
  };

  // ---- Handle bank search
  const handleBankSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBankSearchQuery(e.target.value);
    setShowBankDropdown(true);
  };

  // ---- Handle bank selection
  const handleBankSelect = (bank: any) => {
    setDetails((prev) => ({
      ...prev,
      providerName: bank.name,
      bankCode: bank.code,
      paymentMethod: "BANK_TRANSFER",
      accountName: "",
      accountNumber: "",
    }));
    setBankSearchQuery(bank.name);
    setShowBankDropdown(false);
    setAccountNameVerified(false);
  };

  // ---- Remove selected bank
  const handleRemoveSelectedBank = () => {
    setDetails((prev) => ({
      ...prev,
      providerName: "",
      bankCode: "",
      accountName: "",
      accountNumber: "",
    }));
    setBankSearchQuery("");
    setAccountNameVerified(false);
  };

  // ---- Handle provider selection
  const handleProviderSelect = (providerName: string) => {
    setDetails((prev) => ({
      ...prev,
      providerName,
      paymentMethod: "MOBILE_MONEY",
      network: providerName,
    }));
    setShowProviderDropdown(false);
  };

  // --- Auto verify account when conditions are met
  useEffect(() => {
    if (!details.accountNumber) return;
    if (
      paymentMethod === "BANK_TRANSFER" &&
      fromCurrency?.iso2 === "NG" &&
      currency === "NGN" &&
      details.bankCode &&
      details.accountNumber.length >= 10 &&
      !accountNameVerified &&
      !isVerifyingAccount
    ) {
      const debounceTimer = setTimeout(() => {
        verifyBankAccount();
      }, 1000);

      return () => clearTimeout(debounceTimer);
    }
  }, [details.bankCode, details.accountNumber]);

  // ---- Validate Nigerian bank transfer
  const validateNigerianBankTransfer = () => {
    if (!accountNameVerified) {
      setError("Please verify your account details");
      return false;
    }
    return true;
  };

  // ---- Handle form submission
//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();

//     setError("");

//     // Validate based on payment method
//     if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
//       if (!details.walletAddress || !details.network) {
//         setError("Please fill all required fields");
//         return;
//       }
//     } else if (paymentMethod && EMAIL_PAYMENT_METHODS.has(paymentMethod)) {
//       if (!details.email || !details.accountName) {
//         setError("Please fill all required fields");
//         return;
//       }

//       const isValid = validateEmail(details.email);
//       if (!isValid) {
//         setError("Please enter a valid email address");
//         return;
//       }
//     } else if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
//       // Mobile Money validation
//       if (
//         !details.accountName ||
//         !details.accountNumber ||
//         !details.providerName
//       ) {
//         setError("Please fill all required fields");
//         return;
//       }
//     } else if (paymentMethod === PAYMENT_METHOD.BANK_TRANSFER) {
//       if (!details.accountName || !details.accountNumber) {
//         setError("Please fill all required fields");
//         return;
//       }

//       if (fromCurrency?.iso2 === "NG" && currency === "NGN") {
//         if (!validateNigerianBankTransfer()) return;
//       } else if (fromCurrency?.iso2 === "US" || fromCurrency?.iso2 === "GB") {
//         if (!details.bankCode) {
//           setError(
//             `Please provide ${
//               fromCurrency?.iso2 === "US" ? "Routing No." : "Sort Code"
//             }`
//           );
//           return;
//         }
//       }
//     } else {
//       if (!details.accountName || !details.accountNumber) {
//         setError("Please fill all required fields");
//         return;
//       }
//     }

//     if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
//       if (!details.providerName) {
//         setError("Please select a mobile network");
//         return;
//       }
//     }

//     const { saveDetails: _, holderCity, holderStreet, holderPostalCode, ...payloadDetails } = details;

//     const fullDetails: PaymentDetails = {
//       ...payloadDetails,
//       currency: currency,
//       paymentMethod: paymentMethod || "BANK_TRANSFER",
//       ...(paymentMethod === PAYMENT_METHOD.MOBILE_MONEY && {
//         network: details.providerName,
//       }),
//       ...(paymentMethod === PAYMENT_METHOD.BANK_TRANSFER && {
//         bankCode: details.bankCode,
//         ...(fromCurrency?.iso2 === "US" && { bankCodeLabel: "Routing No." }),
//         ...(fromCurrency?.iso2 === "GB" && { bankCodeLabel: "Sort Code" }),
//     ...((fromCurrency?.iso2 === "GB" || currency === "EUR") &&
//   holderCity && holderStreet && holderPostalCode && {
//   holderCity,
//   holderStreet,
//   holderPostalCode,
// }),
//       }),
//     };

//     onSave(fullDetails);
//     handleClose();
//   };


const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");

  // ---- Handle Alipay/WeChat first — before all other validation
  if (
    paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
    paymentMethod === PAYMENT_METHOD.ALIPAY
  ) {
    if (!details.accountName || !details.phoneNumber) {
      setError("Please fill all required fields");
      return;
    }
    onSave({
      accountName: details.accountName,
      phoneNumber: `+86${details.phoneNumber}`,
      paymentMethod: paymentMethod,
      currency: currency,
      iso2: details.iso2,
    });
    handleClose();
    return;
  }

  // ---- Validate based on payment method
  if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
    if (!details.walletAddress || !details.network) {
      setError("Please fill all required fields");
      return;
    }
  } else if (paymentMethod && EMAIL_PAYMENT_METHODS.has(paymentMethod)) {
    if (!details.email || !details.accountName) {
      setError("Please fill all required fields");
      return;
    }
    const isValid = validateEmail(details.email);
    if (!isValid) {
      setError("Please enter a valid email address");
      return;
    }
  } else if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
    if (!details.accountName || !details.accountNumber || !details.providerName) {
      setError("Please fill all required fields");
      return;
    }
  } else if (paymentMethod === PAYMENT_METHOD.BANK_TRANSFER) {
    if (!details.accountName || !details.accountNumber) {
      setError("Please fill all required fields");
      return;
    }
    if (fromCurrency?.iso2 === "NG" && currency === "NGN") {
      if (!validateNigerianBankTransfer()) return;
    } else if (fromCurrency?.iso2 === "US" || fromCurrency?.iso2 === "GB") {
      if (!details.bankCode) {
        setError(`Please provide ${fromCurrency?.iso2 === "US" ? "Routing No." : "Sort Code"}`);
        return;
      }
    }
  } else {
    if (!details.accountName || !details.accountNumber) {
      setError("Please fill all required fields");
      return;
    }
  }

  if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
    if (!details.providerName) {
      setError("Please select a mobile network");
      return;
    }
  }

  const { saveDetails: _, holderCity, holderStreet, holderPostalCode, ...payloadDetails } = details;

  const fullDetails: PaymentDetails = {
    ...payloadDetails,
    currency: currency,
    paymentMethod: paymentMethod || "BANK_TRANSFER",
    ...(paymentMethod === PAYMENT_METHOD.MOBILE_MONEY && {
      network: details.providerName,
    }),
    ...(paymentMethod === PAYMENT_METHOD.BANK_TRANSFER && {
      bankCode: details.bankCode,
      ...(fromCurrency?.iso2 === "US" && { bankCodeLabel: "Routing No." }),
      ...(fromCurrency?.iso2 === "GB" && { bankCodeLabel: "Sort Code" }),
      ...((fromCurrency?.iso2 === "GB" || currency === "EUR") &&
        holderCity && holderStreet && holderPostalCode && {
          holderCity,
          holderStreet,
          holderPostalCode,
        }),
    }),
  };

  onSave(fullDetails);
  handleClose();
};

  useEffect(() => {
    if (
      isOpen &&
      paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY &&
      exchangeRate?.paymentDetails
    ) {
      const stableCoinDetails = exchangeRate.paymentDetails.filter(
        (detail: PaymentDetails) => detail.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY
      );

      // ---- Auto-select if there's only one network and no network is currently selected
      if (stableCoinDetails.length === 1 && !details.network) {
        setDetails((prev) => ({
          ...prev,
          network: getNetworkCode(stableCoinDetails[0].network),
        }));
      }
    }
  }, [isOpen, paymentMethod, exchangeRate, details.network]);

  // ---- Rendering of Nigerian bank transfer fields
  const renderNigerianBankTransferFields = () => (
    <>
      <div className="relative" ref={bankSearchRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Select Bank
        </label>
        {details.bankCode ? (
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700">
            <span className="font-medium">{details.providerName}</span>
            <button
              type="button"
              onClick={handleRemoveSelectedBank}
              className="text-gray-500 hover:text-gray-700"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={bankSearchQuery}
              onChange={handleBankSearchChange}
              onFocus={() => setShowBankDropdown(true)}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Search for your bank"
              required
            />
            {showBankDropdown && filteredBanks.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto dark:bg-gray-800 dark:border-gray-700">
                {filteredBanks.map((bank) => (
                  <div
                    key={bank.code}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-700"
                    onClick={() => handleBankSelect(bank)}
                  >
                    {bank.name}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Number
          <span className="float-right text-xs text-gray-500">
            {details.accountNumber?.length}/10
          </span>
        </label>
        <input
          type="text"
          name="accountNumber"
          value={details.accountNumber}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter account number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={10}
          minLength={10}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Name
        </label>
        <div className="relative">
          <input
            type="text"
            name="accountName"
            value={details.accountName}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
              accountNameVerified ? "bg-green-50 dark:bg-green-900/20" : ""
            }`}
            placeholder="Enter account name"
            required
            disabled={accountNameVerified}
          />
          {isVerifyingAccount && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Spinner className="h-5 w-5 text-blue-500" />
            </div>
          )}
        </div>
        {accountNameVerified && (
          <p className="text-xs text-green-600 mt-1 dark:text-green-400">
            Account name verified
          </p>
        )}
      </div>
    </>
  );

  // ---- Rendering of international bank fields
  const renderInternationalBankFields = () => {
    // ---- Get the field label and account number max length
        const { label: fieldLabel, maxLength: accountNumberMaxLength,isAlphaNumeric } =
        INT_BANK_FIELD_CONFIG[details.iso2] || INT_BANK_FIELD_CONFIG.default;
        const showAddressFields = details.iso2 === "GB" || currency === "EUR"; 

    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            {fieldLabel}
          </label>
          <input
            type="text"
            name="bankCode"
            value={details.bankCode || ""}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder={`Enter ${fieldLabel}`}
            inputMode={isAlphaNumeric ? "text" : "numeric"}
            pattern={isAlphaNumeric ? undefined : "[0-9]*"}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            Account Number
            <span className="float-right text-xs text-gray-500">
              {details.accountNumber?.length}/{accountNumberMaxLength}
            </span>
          </label>
          <input
            type="text"
            name="accountNumber"
            value={details.accountNumber}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Enter account number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={accountNumberMaxLength}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            Account Name
          </label>
          <input
            type="text"
            name="accountName"
            value={details.accountName}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Enter account name"
            required
          />
        </div>
         {showAddressFields && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Bank <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              name="providerName"
              value={details.providerName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Enter Bank (Optional)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              City
            </label>
            <input
              type="text"
              name="holderCity"
              value={details.holderCity || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Enter city"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Street
            </label>
            <input
              type="text"
              name="holderStreet"
              value={details.holderStreet || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Enter street"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Postal Code
            </label>
            <input
              type="text"
              name="holderPostalCode"
              value={details.holderPostalCode || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Enter Postal Code"
              required
            />
          </div>
        </>
      )}
      </>
    );
  };

  // ---- Rendering of US or GB bank field
  const renderMobileMoneyFields = () => {
    const phoneNumberLength = fromCurrency?.paymentMethod?.phoneNumberLength ?? 10;
    console.log("phoneeee:::S::S", fromCurrency)
    return(
    <>
      <div className="relative" ref={providerDropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Select Network
        </label>
        <button
          type="button"
          className="w-full px-4 py-3 pr-10 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center"
          onClick={() => setShowProviderDropdown(!showProviderDropdown)}
          disabled={loadingProviders}
        >
          {details.providerName ? (
            <>
              {providers.find((p) => p.name === details.providerName)?.logo && (
                <img
                  src={
                    providers.find((p) => p.name === details.providerName)?.logo
                  }
                  alt={details.providerName}
                  className="w-5 h-5 mr-2"
                />
              )}
              <span>{details.providerName}</span>
            </>
          ) : (
            <span className="text-gray-400">Select your mobile network</span>
          )}
          <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
          {loadingProviders && (
            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
              <Spinner className="h-5 w-5 text-blue-500" />
            </div>
          )}
        </button>

        {showProviderDropdown && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border dark:border-gray-700 max-h-60 overflow-auto">
            {providers.map((provider) => (
              <div
                key={provider.code}
                className={`flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                  details.providerName === provider.name
                    ? "bg-gray-100 dark:bg-gray-700"
                    : ""
                }`}
                onClick={() => handleProviderSelect(provider.name)}
              >
                {provider.logo && (
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    className="w-5 h-5 mr-2"
                  />
                )}
                <span>{provider.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Mobile Money Number
          <span className="float-right text-xs text-gray-500">
            {details.accountNumber?.length}/{phoneNumberLength}
          </span>
        </label>
        <input
          type="tel"
          name="accountNumber"
          value={details.accountNumber}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter mobile number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={phoneNumberLength}
          minLength={8}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Mobile Money Name
        </label>
        <input
          type="text"
          name="accountName"
          value={details.accountName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter name on mobile money account"
          required
        />
      </div>
    </>
  )
};

   const renderStableCoinFields = () => {
    const stableCoinDetails =
      exchangeRate?.paymentDetails?.filter(
        (detail: PaymentDetails) => detail.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY
      ) || [];

    const hasMultipleNetworks = stableCoinDetails.length > 1;
    const hasSingleNetwork = stableCoinDetails.length === 1;
    const selectedNetwork = details.network;

    return (
      <>
        {/* Network Selection */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            {hasMultipleNetworks ? "Select Network" : "Network"}
          </label>

          {hasSingleNetwork ? (
            <input
              type="text"
              value={
                getBlockchainName(
                  getNetworkCode(stableCoinDetails[0].network)
                ) || stableCoinDetails[0].network
              }
              readOnly
              disabled
              className="w-full px-3 py-2.5 text-sm bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white cursor-not-allowed sm:px-4 sm:py-3 sm:text-base"
            />
          ) : (
            <div className="relative">
              <select
                name="network"
                value={details.network}
                onChange={(e) => {
                  setDetails((prev) => ({
                    ...prev,
                    network: e.target.value,
                    walletAddress: "",
                  }));
                }}
                className="w-full px-3 py-2.5 pr-8 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white appearance-none sm:px-4 sm:py-3 sm:pr-10 sm:text-base"
                required
              >
                <option value="">Select a network</option>
                {stableCoinDetails.map((detail: PaymentDetails) => (
                  <option
                    key={detail._id}
                    value={getNetworkCode(detail.network)}
                  >
                    {getBlockchainName(getNetworkCode(detail.network))}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-700 dark:text-gray-300">
                <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>
          )}
        </div>

        {/* Wallet Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            Wallet Address
          </label>
          <input
            type="text"
            name="walletAddress"
            value={details.walletAddress}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Provide us with the Address you are sending from"
            required
          />
        </div>

        {/* Warning Message - Show when network is selected */}
        {selectedNetwork && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ Please use the{" "}
              <span className="font-semibold">
                {getBlockchainName(selectedNetwork)}
              </span>{" "}
              network for your payment. Sending via another network will result
              in loss of funds.
            </p>
          </div>
        )}

        {/* Info message for single network */}
        {hasSingleNetwork && !selectedNetwork && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Only one network available:{" "}
              <span className="font-semibold">
                {getBlockchainName(
                  getNetworkCode(stableCoinDetails[0].network)
                )}
              </span>
            </p>
          </div>
        )}
      </>
    );
  };

  const renderEmailFields = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Name
        </label>
        <input
          type="text"
          name="accountName"
          value={details.accountName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter account name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Email Address
        </label>
        <input
          type="email"
          name="email"
          value={details.email || ""}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter email address"
          required
        />
      </div>
    </>
  );


  const renderAlipayWechatFields = () => (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
        Account Name
      </label>
      <input
        type="text"
        name="accountName"
        value={details.accountName}
        onChange={handleInputChange}
        className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        placeholder="Enter account name"
        required
      />
    </div>
    {/* <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
        Phone Number
      </label>
      <input
        type="text"
        name="phoneNumber"
        value={details.phoneNumber || ""}
        onChange={handleInputChange}
        className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        placeholder="Enter Phone Number"
        required
      />
    </div> */}

    <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Phone Number
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500 dark:text-gray-400">+86</span>
          </div>
          <input
            type="tel"
            name="phoneNumber"
            value={details.phoneNumber || ""}
            onChange={handleInputChange}
            placeholder="Enter phone number"
            className="w-full rounded-lg border border-gray-200 bg-white pl-16 pr-4 py-3 text-gray-900 transition-colors focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-primary/50"
            maxLength={11}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {`Enter a valid 11-digit Phone number (without +86).`}
        </p>
      </div>
  </>
);

  // ---- The render that picks which field to show
  const renderFormFields = () => {
    if (!paymentMethod) return null;

      if (paymentMethod === PAYMENT_METHOD.WECHAT_PAY || paymentMethod === PAYMENT_METHOD.ALIPAY) {
    return renderAlipayWechatFields();
  }
    
        if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
          return renderMobileMoneyFields();
        }

        if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
          return renderStableCoinFields();
        }
    
        if (paymentMethod === PAYMENT_METHOD.BANK_TRANSFER) {
          if (fromCurrency?.hasBankList) {
            return renderNigerianBankTransferFields();
          }

          if (details?.iso2 && INTERNATIONAL_BANK_ISO2.has(details?.iso2)) {
            return renderInternationalBankFields();
          }
          // Default bank transfer fields if no specific country is matched
          return (
            <>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Bank Name
                </label>
                <input
                  type="text"
                  name="providerName"
                  value={details.providerName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="Enter bank name"
                  required
                />
              </div> */}


              <div>
  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
    {INT_BANK_FIELD_CONFIG[details.iso2]?.label || "Bank Name"}
  </label>
  <input
    type="text"
    name="providerName"
    value={details.providerName}
    onChange={handleInputChange}
    className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
    placeholder={`Enter ${INT_BANK_FIELD_CONFIG[details.iso2]?.label || "bank name"}`}
    required
  />
</div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Account Number
                </label>
                <input
                  type="text"
                  name="accountNumber"
                  value={details.accountNumber}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="Enter account number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Account Name
                </label>
                <input
                  type="text"
                  name="accountName"
                  value={details.accountName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="Enter account name"
                  required
                />
              </div>
            </>
          );
        }

        if (paymentMethod && EMAIL_PAYMENT_METHODS.has(paymentMethod)) {
          return renderEmailFields();
        }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-md">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            Sending From - {currency}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-8 w-8 text-blue-500" />
          </div>
        ) : step === "select" ? (
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
              Click to input the account details you will be sending from
            </h4>

            <button
              onClick={() => setStep("form")}
              className="w-full py-3 px-4 bg-[#EB2E67] text-white font-medium rounded-lg mt-4"
            >
              Input Account Details
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                {paymentMethod === PAYMENT_METHOD.MOBILE_MONEY
                  ? "Mobile Money Details" : EMAIL_PAYMENT_METHODS.has(paymentMethod)
                  ? "Email Details"
                  : "Bank Details"}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {paymentMethod === PAYMENT_METHOD.MOBILE_MONEY
                  ? "Provide us with your mobile money details" : EMAIL_PAYMENT_METHODS.has(paymentMethod)
                  ? `Provide us with your ${paymentMethod} details`
                  : "Provide us with your bank details"}
              </p>

              {error && (
                <div className="p-2 text-red-600 bg-red-50 rounded-md text-sm">
                  {error}
                </div>
              )}

              {renderFormFields()}

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    paymentMethod === "BANK_TRANSFER" &&
                    fromCurrency?.iso2 === "NG" &&
                    currency === "NGN" &&
                    !accountNameVerified
                  }
                >
                  {isVerifyingAccount ? "Verifying..." : "Continue"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
